import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ListFilterIcon } from "lucide-react";

import Layout from "../../components/Layout";
import ManagerMonthStats from "@/components/ManagerMonthStats";
import { Heading } from "@/components/typography";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useSkeletonPhases } from "@/hooks/useSkeletonPhases";
import { canCreateCampaign } from "../../utils/campaignPermissions";
import { canBrowseOpportunities } from "../../utils/pitching/permissions";
import { OpportunityCardGrid } from "./components/OpportunityCard";
import { OpportunityFilterSheet } from "./components/OpportunityFilterSheet";
import {
    emptyOpportunityListFilter,
    opportunityFilterCount,
    opportunityMatchesListFilter,
    opportunityMatchesSearch,
    sortOpportunitiesByRecency,
    type OpportunityListFilter,
} from "./list";
import { useOpportunities } from "./useOpportunities";

export default function Opportunities() {
    const navigate = useNavigate();
    const page = useOpportunities();
    const [search, setSearch] = useState("");
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [listFilter, setListFilter] = useState<OpportunityListFilter>(emptyOpportunityListFilter);
    const canCreate = canCreateCampaign(page.user);
    const filterCount = opportunityFilterCount(listFilter);

    const openCampaigns = useMemo(() => {
        const matched = page.openCampaigns.filter((campaign) =>
            opportunityMatchesSearch(campaign, search)
            && opportunityMatchesListFilter(campaign, listFilter, sectorOf(campaign, page.sectorByBookerUid)));
        return sortOpportunitiesByRecency(matched);
    }, [listFilter, page.openCampaigns, page.sectorByBookerUid, search]);

    const closedCampaigns = useMemo(() => {
        const matched = page.closedCampaigns.filter((campaign) =>
            opportunityMatchesSearch(campaign, search)
            && opportunityMatchesListFilter(campaign, listFilter, sectorOf(campaign, page.sectorByBookerUid)));
        return sortOpportunitiesByRecency(matched);
    }, [listFilter, page.closedCampaigns, page.sectorByBookerUid, search]);

    const filtering = search.trim().length > 0 || filterCount > 0;
    const showEmptyCopy = openCampaigns.length === 0 && closedCampaigns.length === 0;
    const phase = useSkeletonPhases(page.loading);

    if (page.user && !canBrowseOpportunities(page.user)) {
        return <Navigate to="/" replace />;
    }

    return (
        <Layout>
            {page.isManager ? (
                <ManagerMonthStats
                    pitches={page.pitches}
                    loading={page.pitchesLoading}
                    actionLabel="View My Pitches"
                    actionTo="/my-pitches"
                />
            ) : null}
            <div className="px-4 py-12">
                {/* Title stays left; search, filter, and Create stay on the right. */}
                <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
                    <Heading type="h2" tag="h1" className="font-bold leading-none">
                        Active Opportunities
                    </Heading>
                    <div className="flex flex-wrap items-center gap-2">
                        {canCreate ? (
                            <Button type="button" onClick={() => navigate("/new-opportunity")}>
                                Create
                            </Button>
                        ) : null}
                        <Input
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder="Search Campaigns"
                            aria-label="Search campaigns"
                            className="w-full bg-white sm:w-80"
                        />
                        <Button
                            type="button"
                            variant="secondary"
                            className="relative"
                            onClick={() => setFiltersOpen(true)}
                        >
                            <ListFilterIcon data-icon="inline-start" />
                            Filter
                            {filterCount > 0 ? (
                                <Badge variant="blue" className="pointer-events-none absolute -top-2 -right-3">
                                    {filterCount}
                                </Badge>
                            ) : null}
                        </Button>
                    </div>
                </div>
                {phase === "showSkeleton" ? (
                    <OpportunityCardGrid
                        campaigns={[]}
                        sectorByBookerUid={page.sectorByBookerUid}
                        isLoading
                    />
                ) : phase === "showResult" && (
                    <>
                        {openCampaigns.length > 0 ? (
                            <OpportunityCardGrid campaigns={openCampaigns} sectorByBookerUid={page.sectorByBookerUid} />
                        ) : showEmptyCopy ? (
                            <div className={filtering ? "px-4 py-16 text-center italic" : "px-4 py-16 text-center"}>
                                {filtering
                                    ? (search.trim() && filterCount === 0
                                        ? "No opportunities match your search."
                                        : "No opportunities match your filters.")
                                    : canCreate
                                        ? "You don't have any open opportunities yet... why not add a new one?"
                                        : "You don't have any open opportunities yet."}
                            </div>
                        ) : null}
                        {closedCampaigns.length > 0 ? (
                            <>
                                <Heading
                                    type="h2"
                                    tag="h2"
                                    className={openCampaigns.length > 0 || showEmptyCopy
                                        ? "mt-10 mb-8 font-bold leading-none"
                                        : "mb-8 font-bold leading-none"}
                                >
                                    Recently closed
                                </Heading>
                                <OpportunityCardGrid
                                    campaigns={closedCampaigns}
                                    sectorByBookerUid={page.sectorByBookerUid}
                                />
                            </>
                        ) : null}
                    </>
                )}
            </div>
            <OpportunityFilterSheet
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                value={listFilter}
                onChange={setListFilter}
            />
        </Layout>
    );
}

function sectorOf(campaign: { booker?: { uid?: string } }, sectorByBookerUid: Map<string, string>): string | undefined {
    return campaign.booker?.uid ? sectorByBookerUid.get(campaign.booker.uid) : undefined;
}
